'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { parametricosService } from '@/lib/api/parametricosService';
import { Parametrico, ParametricoUpdate } from '@/types/parametrico';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';

const CATEGORIAS_LABELS: Record<string, string> = {
  capacidad: 'Capacidad de Pago',
  seguros: 'Seguros',
  frecuencias: 'Frecuencias',
  tasas: 'Tasas',
  empresas: 'Empresas',
  buro: 'Buro',
};

const TIPO_DATO_LABELS: Record<string, string> = {
  dict_simple: 'Diccionario Simple',
  dict_anidado: 'Diccionario Anidado',
  lista: 'Lista',
};

interface EditModalProps {
  parametrico: Parametrico;
  onSave: (clave: string, data: ParametricoUpdate) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

const EditModal: React.FC<EditModalProps> = ({ parametrico, onSave, onClose, saving }) => {
  const [nombre, setNombre] = useState(parametrico.nombre);
  const [descripcion, setDescripcion] = useState(parametrico.descripcion || '');
  const [valorJson, setValorJson] = useState(JSON.stringify(parametrico.valor, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'json' | 'visual'>('visual');

  // Para editor visual de dict_simple
  const [keyValuePairs, setKeyValuePairs] = useState<Array<{ key: string; value: string }>>([]);

  useEffect(() => {
    if (parametrico.tipo_dato === 'dict_simple' && typeof parametrico.valor === 'object') {
      const pairs = Object.entries(parametrico.valor as Record<string, unknown>).map(([k, v]) => ({
        key: k,
        value: String(v),
      }));
      setKeyValuePairs(pairs);
    }
  }, [parametrico]);

  const validateJson = useCallback((json: string): boolean => {
    try {
      JSON.parse(json);
      setJsonError(null);
      return true;
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'JSON invalido');
      return false;
    }
  }, []);

  const handleJsonChange = (value: string) => {
    setValorJson(value);
    validateJson(value);
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(valorJson);
      setValorJson(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'JSON invalido');
    }
  };

  const handleKeyValueChange = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...keyValuePairs];
    updated[index][field] = newValue;
    setKeyValuePairs(updated);
    // Sincronizar con JSON
    const obj: Record<string, unknown> = {};
    updated.forEach(({ key, value }) => {
      if (key) {
        // Intentar parsear como numero
        const numVal = parseFloat(value);
        obj[key] = isNaN(numVal) ? value : numVal;
      }
    });
    setValorJson(JSON.stringify(obj, null, 2));
  };

  const handleAddPair = () => {
    setKeyValuePairs([...keyValuePairs, { key: '', value: '' }]);
  };

  const handleRemovePair = (index: number) => {
    const updated = keyValuePairs.filter((_, i) => i !== index);
    setKeyValuePairs(updated);
    // Sincronizar con JSON
    const obj: Record<string, unknown> = {};
    updated.forEach(({ key, value }) => {
      if (key) {
        const numVal = parseFloat(value);
        obj[key] = isNaN(numVal) ? value : numVal;
      }
    });
    setValorJson(JSON.stringify(obj, null, 2));
  };

  const handleSave = async () => {
    if (!validateJson(valorJson)) return;

    const parsed = JSON.parse(valorJson);
    await onSave(parametrico.clave, {
      nombre: nombre !== parametrico.nombre ? nombre : undefined,
      descripcion: descripcion !== (parametrico.descripcion || '') ? descripcion : undefined,
      valor: parsed,
    });
  };

  const canUseVisualEditor = parametrico.tipo_dato === 'dict_simple';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Editar Parametrico</h2>
            <p className="text-sm text-gray-500">{parametrico.clave}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6 space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Descripcion */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Info */}
          <div className="flex gap-4 text-sm text-gray-500">
            <span>Tipo: {TIPO_DATO_LABELS[parametrico.tipo_dato] || parametrico.tipo_dato}</span>
            <span>Categoria: {CATEGORIAS_LABELS[parametrico.categoria] || parametrico.categoria}</span>
            <span>Version: {parametrico.version}</span>
          </div>

          {/* Tabs para editor */}
          {canUseVisualEditor && (
            <div className="flex gap-2 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('visual')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'visual'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Editor Visual
              </button>
              <button
                onClick={() => setActiveTab('json')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'json'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                JSON
              </button>
            </div>
          )}

          {/* Editor Visual para dict_simple */}
          {canUseVisualEditor && activeTab === 'visual' && (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
                <div className="col-span-5">Clave</div>
                <div className="col-span-5">Valor</div>
                <div className="col-span-2"></div>
              </div>
              {keyValuePairs.map((pair, index) => (
                <div key={index} className="grid grid-cols-12 gap-2">
                  <input
                    type="text"
                    value={pair.key}
                    onChange={(e) => handleKeyValueChange(index, 'key', e.target.value)}
                    placeholder="Clave"
                    className="col-span-5 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={pair.value}
                    onChange={(e) => handleKeyValueChange(index, 'value', e.target.value)}
                    placeholder="Valor"
                    className="col-span-5 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => handleRemovePair(index)}
                    className="col-span-2 px-2 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddPair}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                + Agregar campo
              </button>
            </div>
          )}

          {/* Editor JSON */}
          {(!canUseVisualEditor || activeTab === 'json') && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">Valor (JSON)</label>
                <button
                  onClick={handleFormatJson}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Formatear JSON
                </button>
              </div>
              <textarea
                value={valorJson}
                onChange={(e) => handleJsonChange(e.target.value)}
                className={`w-full min-h-[300px] font-mono text-xs p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  jsonError
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {jsonError && (
                <p className="text-sm text-red-600">{jsonError}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !!jsonError}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ParametricosManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<Parametrico[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingParametrico, setEditingParametrico] = useState<Parametrico | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedClaves, setExpandedClaves] = useState<Set<string>>(new Set());
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [invalidatingCache, setInvalidatingCache] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await parametricosService.getAll();
    if (res.data) {
      setItems(res.data);
    } else {
      setError(res.error || 'Error al cargar parametricos');
    }
    setLoading(false);
  };

  // Esperar a que la autenticación esté lista antes de cargar
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const handleSave = async (clave: string, data: ParametricoUpdate) => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await parametricosService.update(clave, data);
      if (res.error) {
        setError(res.error);
      } else {
        setSuccessMessage(`Parametrico "${clave}" actualizado correctamente`);
        setEditingParametrico(null);
        await load();
      }
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : 'Error al guardar';
      setError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleInvalidateCache = async () => {
    setInvalidatingCache(true);
    setError(null);
    const res = await parametricosService.invalidateCache();
    if (res.error) {
      setError(res.error);
    } else {
      setSuccessMessage('Cache invalidado correctamente. Los nuevos valores se aplicaran en los proximos calculos.');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
    setInvalidatingCache(false);
  };

  const handleSeedData = async () => {
    setSaving(true);
    setError(null);
    const res = await parametricosService.seed();
    if (res.error) {
      setError(res.error);
    } else if (res.data) {
      setSuccessMessage(res.data.message);
      await load();
    }
    setSaving(false);
  };

  const toggleExpanded = (clave: string) => {
    setExpandedClaves((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) {
        next.delete(clave);
      } else {
        next.add(clave);
      }
      return next;
    });
  };

  // Agrupar por categoria
  const grouped = items.reduce((acc, item) => {
    const cat = item.categoria || 'otros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {} as Record<string, Parametrico[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parametricos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Administracion de valores configurables del sistema.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHelpModal(true)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Ayuda"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <Button variant="secondary" onClick={load}>
            Recargar
          </Button>
          <Button
            variant="secondary"
            onClick={handleInvalidateCache}
            disabled={invalidatingCache}
          >
            {invalidatingCache ? 'Invalidando...' : 'Invalidar Cache'}
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-800">
            &times;
          </button>
        </div>
      )}

      {/* Estado vacío - sin datos */}
      {items.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <svg
              className="mx-auto h-16 w-16 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No hay parametricos configurados
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              La tabla de parametricos esta vacia. Carga los datos iniciales desde el archivo de configuracion legacy.
            </p>
            <div className="mt-6">
              <Button onClick={handleSeedData} disabled={saving}>
                {saving ? 'Cargando...' : 'Cargar Datos Iniciales'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([categoria, parametricos]) => (
        <div key={categoria} className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {CATEGORIAS_LABELS[categoria] || categoria}
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {parametricos.map((p) => {
              const isExpanded = expandedClaves.has(p.clave);
              const jsonPreview = JSON.stringify(p.valor, null, 2);
              const isLong = jsonPreview.length > 200 || jsonPreview.split('\n').length > 5;

              return (
                <div key={p.clave} className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{p.nombre}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {p.clave}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                          {TIPO_DATO_LABELS[p.tipo_dato] || p.tipo_dato}
                        </span>
                        <span className="text-xs text-gray-400">v{p.version}</span>
                      </div>
                      {p.descripcion && (
                        <p className="text-sm text-gray-400 mt-2">{p.descripcion}</p>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditingParametrico(p)}
                    >
                      Editar
                    </Button>
                  </div>

                  <div className="mt-3">
                    <pre
                      className={`p-3 bg-gray-50 rounded-lg text-xs font-mono overflow-auto ${
                        isLong && !isExpanded ? 'max-h-32' : ''
                      }`}
                    >
                      {jsonPreview}
                    </pre>
                    {isLong && (
                      <button
                        onClick={() => toggleExpanded(p.clave)}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                      >
                        {isExpanded ? 'Ver menos' : 'Ver mas...'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {items.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-sm text-gray-500">
          Total de parametricos: {items.length}
        </div>
      )}

      {/* Modal de edicion */}
      {editingParametrico && (
        <EditModal
          parametrico={editingParametrico}
          onSave={handleSave}
          onClose={() => setEditingParametrico(null)}
          saving={saving}
        />
      )}

      {/* Modal de ayuda */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Como funcionan los Parametricos</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Que son los Parametricos?</h3>
                <p className="text-sm text-gray-600">
                  Los parametricos son valores configurables que controlan como se calculan los creditos,
                  tasas, comisiones y otros aspectos del sistema. Al modificarlos aqui, puedes ajustar
                  el comportamiento del sistema sin necesidad de cambiar codigo.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Como afectan los cambios?</h3>
                <p className="text-sm text-gray-600">
                  Cuando modificas un parametrico, el cambio se guarda en la base de datos. Sin embargo,
                  el sistema usa un <strong>cache de 5 minutos</strong> para optimizar el rendimiento.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Importante:</strong> Despues de hacer cambios, usa el boton &quot;Invalidar Cache&quot;
                    para que los nuevos valores se apliquen inmediatamente en los calculos.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Flujo de carga de valores</h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">1. Cache</span>
                  <span className="text-gray-400">→</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">2. Base de datos</span>
                  <span className="text-gray-400">→</span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full">3. Archivo (fallback)</span>
                </div>
                <p className="text-xs text-gray-500">
                  Si el cache esta vacio o expiro, se consulta la BD. Si falla la BD, se usan los valores del archivo de configuracion.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Parametricos disponibles</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li><strong>Factor de Capacidad de Pago:</strong> Porcentaje del ingreso que se puede destinar al credito segun tipo de empleado (Sindicalizado/No Sindicalizado).</li>
                  <li><strong>Costo de Seguros:</strong> Costo base del seguro por parcialidad segun monto de poliza.</li>
                  <li><strong>Frecuencias:</strong> Numero de pagos por año segun frecuencia (Semanal, Quincenal, etc.).</li>
                  <li><strong>Tasas Base:</strong> Tasas y comisiones base por tipo de producto.</li>
                  <li><strong>Parametricos Buro:</strong> Configuracion de tasas, comisiones y plazos segun calificacion de buro.</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Precaucion:</strong> Los cambios en estos valores afectan directamente los calculos de
                  propuestas, simulaciones y creditos. Asegurate de entender el impacto antes de modificar.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <Button onClick={() => setShowHelpModal(false)}>
                Entendido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametricosManagement;
